
import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common'
import { Component, ViewChild } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { ActivatedRoute } from '@angular/router'
import { ReplaySubject } from 'rxjs'
import {
    AssetType,
    FavouriteType,
    Fees, 
    Listing,
    ListingHotelQuery,
    PublicListingGQL
} from '../../../graphql'
import { Amplitude } from '../../injectable'
import { ListingTravelDatesComponent } from '../../module/shared-member/organism/listing/listing-travel-dates/listing-travel-dates'
import { ListingLink } from '../../module/shared-member/organism/listing/shared/listing-link'
import { TravelDates } from '../../module/shared-member/organism/listing/shared/travel-dates'
import { UnsubscribeOnDestroy, scrollToTop } from '../../utility'
import { HotelSuiteThumbnailData } from '../../module/shared-member/organism/listing/listing-hotel/listing-hotel'
import { CurrencyService } from 'src/app/injectable/currency-service'

@Component({
    selector: 'app-public-listing',
    styleUrls: ['public-listing.scss'],
    templateUrl: 'public-listing.html',
    providers: [Location, { provide: LocationStrategy, useClass: PathLocationStrategy }]
})
export class PublicListingComponent extends UnsubscribeOnDestroy {

    AssetType = AssetType
    FavouriteType = FavouriteType

    listing: Listing | undefined
    listingId: string
    travelDates$: ReplaySubject<TravelDates>
    travelDates: TravelDates | undefined
    openBookingPanel = false
    selectedSuite: ListingHotelQuery['hotelListing']['suites'][0]
    expandMobileFooter = false
    bestRate: string
    hotelSuitesThumbnailData: HotelSuiteThumbnailData[]
    companyLogo: string
    currency: string
    fees: Fees
    lowFeeRange: Fees
    highFeeRange: Fees

    @ViewChild('travelDatesComponent') travelDatesComponent: ListingTravelDatesComponent

    constructor(
        public _amplitude: Amplitude,
        private _activatedRoute: ActivatedRoute,
        private _location: Location,
        private _title: Title,
        private _publicListingGQL: PublicListingGQL,
        private _currencyService: CurrencyService
    ) {
        super()

        this._title.setTitle('Listing')

        this.companyLogo = this._activatedRoute.snapshot.queryParams['company_logo']
        this.currency = this._activatedRoute.snapshot.queryParams['currency']

        if (this.currency) {
            this._currencyService.setPresentmentCurrency(this.currency)
        }

        this._subscriptions.push(this._activatedRoute.paramMap.subscribe(paramMap => {
            // Child components can trigger a URL change. Making sure we reset state and reload listing
            this.listing = undefined
            this.travelDates = undefined
            this.travelDates$ = new ReplaySubject<TravelDates>()
            this.listingId = paramMap.get('listingId') || ''
            this.emit(new TravelDates(undefined, undefined))
        }))
    }

    emit(travelDates: TravelDates) {
        this.travelDates = travelDates

        setTimeout(() => {
            if (this.listingId) {
                const listingLink = new ListingLink(this.listingId, travelDates, true)
                this._location.replaceState(listingLink.toPath() + '?company_logo=' + this.companyLogo)    
            }

            this.requestListing()
        }, 1)
    }

    openMobileBookingPanel(value: boolean, event: Event) {
        event.stopPropagation()
        this.openBookingPanel = value
    }

    updateSelectedSuite(selectedSuite: ListingHotelQuery['hotelListing']['suites'][0]) {
        this.selectedSuite = selectedSuite
        this.listingId = selectedSuite?.hotelSuiteId
        if (this.listingId) {
            const listingLink = new ListingLink(this.listingId, new TravelDates(undefined, undefined), true)
            this._location.replaceState(listingLink.toPath() + '?company_logo=' + this.companyLogo)      
        }
        this.requestListing()
    }

    updateHotelSuitesThumbnails($event: HotelSuiteThumbnailData[]) {
        this.hotelSuitesThumbnailData = $event
    }

    suiteThumbnailSelect(index: number) {
        const tabIndex = index + 2
        const tabEl: HTMLElement = document.querySelectorAll(`[data-tab-button-index='${tabIndex}']`)[0] as HTMLElement
        this.triggerTabClick(tabEl) 
    }

    triggerTabClick(tabEl: HTMLElement) {
        if (tabEl) {
            tabEl.click()
            tabEl.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'end', 
                inline: 'nearest' 
            })

            scrollToTop()
        } 
    }

    setBestRate($event: Fees) {
        this.fees = $event
    }

    private toEventProperties(travelDates: TravelDates | undefined = undefined): any {
        const listing = this.listing
        
        return {
            listingId: listing?.listingId,
            operatorId: listing?.operator?.operatorId,
            operatorName: listing?.operator?.name,
            bookingFees: listing?.fees?.bookingFee?.amountMicros / 1_000_000,
        }
    }

    private requestListing() {
        if (this.listingId) {
            this._subscriptions.push(
                this._publicListingGQL
                    .watch({ listingId: this.listingId }, { fetchPolicy: 'no-cache' })
                    .valueChanges.subscribe(result => {
                        if (!result.loading) {
                            this.listing = result.data.listing as Listing

                            if (this.listing.priceRange?.lowest) {
                                this.lowFeeRange = this.listing.priceRange.lowest
                            }

                            this._title.setTitle(this.listing?.summary.title)
                            this._amplitude.logEvent('Public Listing Page Viewed', this.toEventProperties())
                        }
                    }))
        }
    }
}
